import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import {
  Activity,
  Briefcase,
  ChartLine,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  DollarSign,
  Home,
  LogOut,
  MessageSquare,
  Settings,
  Users,
  X
} from "lucide-react";
import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore.js";

const AdminSidebar = ({ 
  onToggle, 
  isMobile, 
  isSidebarCollapsed, 
  isMobileSidebarOpen, 
  onMobileSidebarToggle 
}) => {
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const sidebarRef = useRef(null);

  const toggleSidebar = () => {
    if (isMobile) {
      onMobileSidebarToggle();
    } else {
      onToggle(!isSidebarCollapsed);
    }
  };

  // Admin navigation items
  const adminNavItems = [
    {
      name: "Dashboard",
      path: "/admin/dashboard",
      icon: Home,
      exact: true
    },
    {
      name: "User Management",
      icon: Users,
      submenu: [
        { name: "All Users", path: "/admin/users", icon: Users },
        // {name: 'Audit trail', path: '/admin/audit', icon: Activity}
        // { name: "Pending Verifications", path: "/admin/verifications", icon: UserCheck, badge: true },
        // { name: "User Analytics", path: "/admin/user-analytics", icon: TrendingUp }
      ]
    },
    {
      name: "Job Management",
      icon: Briefcase,
      submenu: [
        { name: "Job Postings", path: "/admin/jobs", icon: Briefcase },
        { name: "Job Applications", path: "/admin/job-applications", icon: MessageSquare },
        // { name: "User Analytics", path: "/admin/user-analytics", icon: TrendingUp }
      ]
    },
    {
      name: "Subscription Management",
      icon: CreditCard,
      submenu: [
        { name: "Subscriptions", path: "/admin/subscriptions", icon: CreditCard },
        { name: "Subscription Plans", path: "/admin/subscription-plans", icon: MessageSquare },
        { name: "Payments", path: "/admin/subscription-payments", icon: DollarSign }
      ]
    },
    {
      name: 'User Activity',
      icon: Activity,
      submenu: [
        {name: 'Audit trail', path: '/admin/audit', icon: Activity},
        {name: 'Audit statistics', path: '/admin/audit/stats', icon: ChartLine},
        {name: 'Recent Session', path: '/admin/audit/me', icon: Activity},
      ]
    },
    {
      name: "Settings",
      path: "/admin/settings",
      icon: Settings
    }
  ];

  // GSAP animations for desktop sidebar only
  useGSAP(() => {
    if (sidebarRef.current && !isMobile) {
      const sidebar = sidebarRef.current;
      const tl = gsap.timeline();
      
      if (isSidebarCollapsed) {
        tl.to(sidebar, {
          width: 64,
          duration: 0.3,
          ease: "power2.inOut"
        });
      } else {
        tl.to(sidebar, {
          width: 280,
          duration: 0.3,
          ease: "power2.inOut"
        });
      }
    }
  }, [isSidebarCollapsed, isMobile]);

  const handleNavigation = (path) => {
    navigate(path);
    setActiveSubmenu(null);
    if (isMobile) {
      onMobileSidebarToggle();
    }
  };

  const toggleSubmenu = (menuName) => {
    setActiveSubmenu(activeSubmenu === menuName ? null : menuName);
  };

  const isActivePath = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Desktop sidebar - fixed positioning
  if (!isMobile) {
    return (
      <aside
        ref={sidebarRef}
        className="bg-white border-r border-secondary-200 h-screen fixed left-0 top-0 z-40 flex flex-col overflow-hidden shadow-lg"
        style={{ width: isSidebarCollapsed ? "64px" : "280px" }}
      >
        {/* Desktop sidebar content - same as before */}
        {/* Desktop sidebar header */}
<div className="flex items-center justify-between p-4 border-b border-secondary-100">
  <div className="flex items-center flex-1">
    {!isSidebarCollapsed ? (
      <div className="flex items-center space-x-3 flex-1">
        <img src="/logo.png" alt="WinguPort Admin" className="h-8 w-8" />
        <div>
          <span className="text-lg font-bold">
                    <span className="text-wingu-light-blue">Wingu</span>
                    <span className="text-wingu-dark-blue">Port</span>
                    <span className="text-wingu-light-blue ml-2">Admin</span>
                  </span>
          <p className="text-xs text-secondary-500">Control Panel</p>
        </div>
      </div>
    ) : (
      <div className="flex justify-center flex-1">
        <img src="/logo.png" alt="WinguPort Admin" className="h-8 w-8" />
      </div>
    )}
  </div>
  
  <button
    onClick={toggleSidebar}
    className="p-1.5 rounded-lg hover:bg-secondary-100 transition-colors text-secondary-500 hover:text-secondary-700 flex-shrink-0 ml-2"
  >
    {isSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
  </button>
</div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {adminNavItems.map((item, index) => {
              const Icon = item.icon;
              const hasSubmenu = item.submenu;
              const isActive = item.path ? isActivePath(item.path, item.exact) : false;
              const isSubmenuOpen = activeSubmenu === item.name;

              if (hasSubmenu) {
                return (
                  <div key={item.name} className="relative">
                    <button
                      onClick={() => toggleSubmenu(item.name)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-200 group ${
                        isSubmenuOpen
                          ? "bg-primary-50 text-primary-600 border-r-2 border-primary-600"
                          : "text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon
                          size={20}
                          className={`flex-shrink-0 ${
                            isSubmenuOpen ? "text-primary-600" : "text-secondary-500"
                          }`}
                        />
                        {!isSidebarCollapsed && (
                          <span className="font-medium text-sm">{item.name}</span>
                        )}
                      </div>
                      {!isSidebarCollapsed && (
                        <ChevronRight
                          size={16}
                          className={`transition-transform duration-200 ${
                            isSubmenuOpen ? "rotate-90" : ""
                          }`}
                        />
                      )}
                    </button>

                    {!isSidebarCollapsed && isSubmenuOpen && (
                      <div className="ml-8 mt-1 space-y-1 animate-in fade-in-5 slide-in-from-top-2">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = isActivePath(subItem.path, true);
                          
                          return (
                            <button
                              key={subItem.name}
                              onClick={() => handleNavigation(subItem.path)}
                              className={`w-full flex items-center space-x-3 p-2 rounded-lg text-sm transition-colors group ${
                                isSubActive
                                  ? "bg-primary-100 text-primary-700 font-medium"
                                  : "text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                              }`}
                            >
                              <SubIcon
                                size={16}
                                className={`flex-shrink-0 ${
                                  isSubActive ? "text-primary-600" : "text-secondary-400"
                                }`}
                              />
                              <span className="flex-1 text-left">{subItem.name}</span>
                              {subItem.badge && (
                                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full animate-pulse">
                                  •
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center p-3 rounded-lg transition-all duration-200 group ${
                    isActive
                      ? "bg-primary-50 text-primary-600 border-r-2 border-primary-600"
                      : "text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                  }`}
                >
                  <Icon
                    size={20}
                    className={`flex-shrink-0 ${
                      isActive ? "text-primary-600" : "text-secondary-500"
                    }`}
                  />
                  {!isSidebarCollapsed && (
                    <span className="font-medium text-sm ml-3">{item.name}</span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>
            {isMobile && (

        <div className="border-t border-secondary-100 p-4">
          {!isSidebarCollapsed ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <img
                  src={user?.profile_picture || `https://placehold.co/100x100/E2E8F0/475569?text=${user?.first_name?.charAt(0) || 'A'}`}
                  alt="Admin"
                  className="w-10 h-10 rounded-full border-2 border-white shadow-sm"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-secondary-900 truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-secondary-500 truncate">Administrator</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut size={18} />
                <span className="font-medium text-sm">Sign Out</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-3">
              <img
                src={user?.profile_picture || `https://placehold.co/100x100/E2E8F0/475569?text=${user?.first_name?.charAt(0) || 'A'}`}
                alt="Admin"
                className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
              />
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut size={18} />
              </button>
            </div>
          )}
        </div>
            )}
      </aside>
    );
  }

  // Mobile sidebar - only when open
  return (
    <>
      <aside
        className={`fixed left-0 top-0 h-full w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Mobile sidebar content */}
        <div className="flex justify-between items-center p-4 border-b border-secondary-100">
          <div className="flex items-center space-x-3">
            <img src="/logo.png" alt="WinguPort Admin" className="h-8 w-8" />
            <div>
              <h2 className="font-bold text-lg text-secondary-900">Wingu Port Admin</h2>
              <p className="text-xs text-secondary-500">Control Panel</p>
            </div>
          </div>
          <button
            onClick={onMobileSidebarToggle}
            className="p-2 text-secondary-500 hover:text-secondary-700 rounded-full hover:bg-secondary-100"
          >
            <X size={20} />
          </button>
        </div>
        
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {adminNavItems.map((item, index) => {
              const Icon = item.icon;
              const hasSubmenu = item.submenu;
              const isActive = item.path ? isActivePath(item.path, item.exact) : false;
              const isSubmenuOpen = activeSubmenu === item.name;

              if (hasSubmenu) {
                return (
                  <div key={item.name} className="relative">
                    <button
                      onClick={() => toggleSubmenu(item.name)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-200 group ${
                        isSubmenuOpen
                          ? "bg-primary-50 text-primary-600 border-r-2 border-primary-600"
                          : "text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon
                          size={20}
                          className={`flex-shrink-0 ${
                            isSubmenuOpen ? "text-primary-600" : "text-secondary-500"
                          }`}
                        />
                        <span className="font-medium text-sm">{item.name}</span>
                      </div>
                      <ChevronRight
                        size={16}
                        className={`transition-transform duration-200 ${
                          isSubmenuOpen ? "rotate-90" : ""
                        }`}
                      />
                    </button>

                    {isSubmenuOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = isActivePath(subItem.path, true);
                          
                          return (
                            <button
                              key={subItem.name}
                              onClick={() => handleNavigation(subItem.path)}
                              className={`w-full flex items-center space-x-3 p-2 rounded-lg text-sm transition-colors group ${
                                isSubActive
                                  ? "bg-primary-100 text-primary-700 font-medium"
                                  : "text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                              }`}
                            >
                              <SubIcon
                                size={16}
                                className={`flex-shrink-0 ${
                                  isSubActive ? "text-primary-600" : "text-secondary-400"
                                }`}
                              />
                              <span className="flex-1 text-left">{subItem.name}</span>
                              {subItem.badge && (
                                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full animate-pulse">
                                  •
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center p-3 rounded-lg transition-all duration-200 group ${
                    isActive
                      ? "bg-primary-50 text-primary-600 border-r-2 border-primary-600"
                      : "text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900"
                  }`}
                >
                  <Icon
                    size={20}
                    className={`flex-shrink-0 ${
                      isActive ? "text-primary-600" : "text-secondary-500"
                    }`}
                  />
                  <span className="font-medium text-sm ml-3">{item.name}</span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="border-t border-secondary-100 p-4">
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <img
                src={user?.profile_picture || `https://placehold.co/100x100/E2E8F0/475569?text=${user?.first_name?.charAt(0) || 'A'}`}
                alt="Admin"
                className="w-10 h-10 rounded-full border-2 border-white shadow-sm"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-secondary-900 truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-secondary-500 truncate">Administrator</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={18} />
              <span className="font-medium text-sm">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;